import { createBrowserRouter, Navigate } from "react-router-dom";
import { ProtectedRoute } from "../components/auth/ProtectedRoute";
import { PublicOnlyRoute } from "../components/auth/PublicOnlyRoute";
import { AppShell } from "../components/layout/AppShell";
import { FullPageLoader } from "../components/ui/FullPageLoader";
import { ROLE_REDIRECTS, TRAINER_ROLES } from "../lib/constants";
import { useAuth } from "../hooks/useAuth";
import { useTenant } from "../hooks/useTenant";
import { AdminPage } from "../pages/admin/AdminPage";
import { AdminAuditLogsPage } from "../pages/admin/AdminAuditLogsPage";
import { AdminDataHealthPage } from "../pages/admin/AdminDataHealthPage";
import { AdminFeatureFlagsPage } from "../pages/admin/AdminFeatureFlagsPage";
import { AdminInvitesPage } from "../pages/admin/AdminInvitesPage";
import { AdminMetricsPage } from "../pages/admin/AdminMetricsPage";
import { AdminSettingsPage } from "../pages/admin/AdminSettingsPage";
import { AdminTenantDetailPage } from "../pages/admin/AdminTenantDetailPage";
import { AdminTenantsPage } from "../pages/admin/AdminTenantsPage";
import { AdminUsersPage } from "../pages/admin/AdminUsersPage";
import { AuthCallbackPage } from "../pages/auth/AuthCallbackPage";
import { ForgotPasswordPage } from "../pages/auth/ForgotPasswordPage";
import { InvitePage } from "../pages/auth/InvitePage";
import { LoginPage } from "../pages/auth/LoginPage";
import { ResetPasswordPage } from "../pages/auth/ResetPasswordPage";
import { ClientGlossaryPage } from "../pages/client/ClientGlossaryPage";
import { ClientMealsPage } from "../pages/client/ClientMealsPage";
import { ClientOnboardingPage } from "../pages/client/ClientOnboardingPage";
import { ClientPage } from "../pages/client/ClientPage";
import { ClientWorkoutsPage } from "../pages/client/ClientWorkoutsPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { TrainerBusinessPage } from "../pages/trainer/TrainerBusinessPage";
import { TrainerClientDetailPage } from "../pages/trainer/TrainerClientDetailPage";
import { TrainerClientNewPage } from "../pages/trainer/TrainerClientNewPage";
import { TrainerClientsPage } from "../pages/trainer/TrainerClientsPage";
import { TrainerExercisesPage } from "../pages/trainer/TrainerExercisesPage";
import { TrainerGlossaryPage } from "../pages/trainer/TrainerGlossaryPage";
import { TrainerHomePage } from "../pages/trainer/TrainerHomePage";
import { TrainerMealsPage } from "../pages/trainer/TrainerMealsPage";
import { TrainerProfilePage } from "../pages/trainer/TrainerProfilePage";
import { TrainerSettingsPage } from "../pages/trainer/TrainerSettingsPage";
import { TrainerTeamPage } from "../pages/trainer/TrainerTeamPage";
import { TrainerWorkoutsPage } from "../pages/trainer/TrainerWorkoutsPage";

function RootRedirect() {
  const { user, loading: authLoading } = useAuth();
  const { currentRole, loading: tenantLoading } = useTenant();

  if (authLoading || tenantLoading) {
    return <FullPageLoader label="Opening CoachGrid" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!currentRole) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="max-w-2xl">
            <p className="eyebrow">Workspace required</p>
            <h1 className="mt-3 text-3xl font-semibold text-ink">
              Your account is not connected to an active workspace yet.
            </h1>
            <p className="mt-4 text-base leading-7 text-graphite/75">
              Ask your coach, workspace owner or CoachGrid administrator for an
              invitation to the right workspace.
            </p>
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  return <Navigate to={ROLE_REDIRECTS[currentRole]} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootRedirect />,
  },
  {
    path: "/login",
    element: (
      <PublicOnlyRoute>
        <LoginPage />
      </PublicOnlyRoute>
    ),
  },
  {
    path: "/signup",
    element: <Navigate to="/login" replace />,
  },
  {
    path: "/forgot-password",
    element: (
      <PublicOnlyRoute>
        <ForgotPasswordPage />
      </PublicOnlyRoute>
    ),
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/invite/:token",
    element: <InvitePage />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
  },
  {
    path: "/admin",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/tenants",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminTenantsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/tenants/:tenantId",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminTenantDetailPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminUsersPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/invites",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminInvitesPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/metrics",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminMetricsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/data-health",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminDataHealthPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/audit-logs",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminAuditLogsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/feature-flags",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminFeatureFlagsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/settings",
    element: (
      <ProtectedRoute allowedRoles={["platform_admin"]}>
        <AppShell>
          <AdminSettingsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerHomePage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/settings",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerSettingsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/profile",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerProfilePage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/business",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerBusinessPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/team",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerTeamPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/workouts",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerWorkoutsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/meals",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerMealsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/glossary",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerGlossaryPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/exercises",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerExercisesPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/clients",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerClientsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/clients/new",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerClientNewPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/trainer/clients/:clientId",
    element: (
      <ProtectedRoute allowedRoles={TRAINER_ROLES}>
        <AppShell>
          <TrainerClientDetailPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/client/onboarding",
    element: (
      <ProtectedRoute allowedRoles={["client"]}>
        <AppShell>
          <ClientOnboardingPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/client/workouts",
    element: (
      <ProtectedRoute allowedRoles={["client"]}>
        <AppShell>
          <ClientWorkoutsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/client/meals",
    element: (
      <ProtectedRoute allowedRoles={["client"]}>
        <AppShell>
          <ClientMealsPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/client/glossary",
    element: (
      <ProtectedRoute allowedRoles={["client"]}>
        <AppShell>
          <ClientGlossaryPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/client",
    element: (
      <ProtectedRoute allowedRoles={["client"]}>
        <AppShell>
          <ClientPage />
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
